import React, { useState, useEffect, useRef } from 'react';
import { subscribeRoom, handleAnswerTimeout } from '../shared/roomTx';
import {
  FL_PHASE,
  FL_ROOM_STATUS,
  FL_SCRIPT_LABELS,
  FL_DIFFICULTY,
} from '../shared/fiveLeague';
import {
  readQuestion,
  readAnswerReveal,
  announceResult,
  readCorrectAnswer,
  announceNextQuestion,
  announceGameClear,
  testTTS,
  testVoice,
  playSoundEffect,
  GOOGLE_VOICES,
} from '../shared/tts';
import { isTTSEnabled, setTTSEnabled, hasTTSPreference } from '../shared/ttsSettings';
import './TVDisplay.css';

// 文字種判定ヘルパー
function getCharTypeLabel(char) {
  if (!char || char === '＿' || char === '？') return null;
  const c = char.trim();
  if (!c) return null;

  // 小文字判定
  const smallHiragana = 'ぁぃぅぇぉっゃゅょゎ';
  const smallKatakana = 'ァィゥェォッャュョヮ';
  const isSmall = smallHiragana.includes(c) || smallKatakana.includes(c);

  // 文字種判定
  if (/^[ぁ-ん]$/.test(c)) return isSmall ? 'ひ小' : 'ひ';
  if (/^[ァ-ヶー]$/.test(c)) return isSmall ? 'カ小' : 'カ';
  if (/^[一-龯々]$/.test(c)) return '漢';
  if (/^[0-9０-９]$/.test(c)) return '数';
  if (/^[A-Za-zＡ-Ｚａ-ｚ]$/.test(c)) return '英';

  return null;
}

// ═══════════════════════════════════════════════════════════
// TVDisplay: テレビ専用表示画面
// ═══════════════════════════════════════════════════════════

function TVDisplay({ roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [countdownNum, setCountdownNum] = useState(5);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalContent, setResultModalContent] = useState({ isPerfect: false });
  const [ttsReady, setTtsReady] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const pref = isTTSEnabled();
    return pref !== null ? pref : true; // TVはデフォルトON
  });
  const [showSoundPrompt, setShowSoundPrompt] = useState(!hasTTSPreference());
  const [showVoiceTest, setShowVoiceTest] = useState(false);
  const [showMirrorGuide, setShowMirrorGuide] = useState(false);
  const [revealAnimation, setRevealAnimation] = useState(false);
  const timerRef = useRef(null);
  const phaseHandledRef = useRef({});

  // TTS初期化（ユーザーインタラクション後）
  const initTTS = async () => {
    if (ttsReady || !soundEnabled) return;
    console.log('[TVDisplay] Initializing TTS...');
    const success = await testTTS();
    if (success) {
      setTtsReady(true);
      console.log('[TVDisplay] TTS ready!');
    } else {
      console.warn('[TVDisplay] TTS failed to initialize');
    }
  };

  // 音声ON/OFF切り替え
  const toggleSound = async (enabled) => {
    setSoundEnabled(enabled);
    setTTSEnabled(enabled);
    setShowSoundPrompt(false);
    if (enabled && !ttsReady) {
      await initTTS();
    }
  };

  // ルーム購読
  useEffect(() => {
    if (!roomId) return;

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      setRoom(data);
    });

    return () => unsub();
  }, [roomId]);

  // フェーズ変更時の処理
  useEffect(() => {
    if (!room?.game) return;

    const game = room.game;
    const phase = game.phase;
    const questionIndex = game.questionIndex;
    const phaseKey = `${questionIndex}-${phase}`;

    if (phaseHandledRef.current[phaseKey]) return;

    // 新しい問題になったらリセット
    const prevKeys = Object.keys(phaseHandledRef.current);
    const currentQuestionPrefix = `${questionIndex}-`;
    if (prevKeys.some(k => !k.startsWith(currentQuestionPrefix))) {
      phaseHandledRef.current = {};
      setShowResultModal(false);
    }

    switch (phase) {
      case FL_PHASE.READING:
        phaseHandledRef.current[phaseKey] = true;
        setShowResultModal(false);
        setRevealAnimation(false);
        // TV画面で読み上げを実行（音声ONの場合のみ）
        if (soundEnabled && ttsReady) {
          const handleTVReading = async () => {
            const current = game.current;
            const questionNumber = (game.questionIndex || 0) + 1;
            await readQuestion(current, questionNumber);
          };
          handleTVReading();
        }
        // 注意: TTSは自然に完了させるため、クリーンアップでstopSpeakingを呼ばない
        break;

      case FL_PHASE.ANSWERING:
        // 重複防止
        if (!phaseHandledRef.current[phaseKey]) {
          phaseHandledRef.current[phaseKey] = true;
          const endAt = game.current?.answerEndAt || 0;
          const updateTimer = () => {
            const now = Date.now();
            const left = Math.max(0, Math.ceil((endAt - now) / 1000));
            setTimeLeft(left);

            // TV画面からもタイムアウトをトリガー（バックアップ）
            if (left <= 0) {
              clearInterval(timerRef.current);
              if (!phaseHandledRef.current[`${questionIndex}-timeout`]) {
                phaseHandledRef.current[`${questionIndex}-timeout`] = true;
                // 1秒遅延でホストの処理を優先
                setTimeout(() => {
                  handleAnswerTimeout(roomId);
                }, 1000);
              }
            }
          };
          updateTimer();
          timerRef.current = setInterval(updateTimer, 200);
        }
        break;

      case FL_PHASE.REVEAL_COUNTDOWN:
        phaseHandledRef.current[phaseKey] = true;
        clearInterval(timerRef.current);
        setRevealAnimation(false);

        // サーバーのタイムスタンプを使ってカウントダウンを同期
        const revealOpenAt = game.current?.revealOpenAt || 0;
        const updateCountdown = () => {
          const now = Date.now();
          const remaining = Math.max(0, Math.ceil((revealOpenAt - now) / 1000));
          setCountdownNum(remaining);

          if (remaining <= 0) {
            clearInterval(timerRef.current);
            // カウントダウン終了時に効果音（音声ONの場合のみ）
            if (soundEnabled) {
              playSoundEffect('timeup');
            }
          }
        };
        updateCountdown(); // 即座に1回実行
        timerRef.current = setInterval(updateCountdown, 200); // 200msごとに更新（滑らかに）
        return () => clearInterval(timerRef.current);

      case FL_PHASE.REVEALED:
        phaseHandledRef.current[phaseKey] = true;
        clearInterval(timerRef.current);
        // 回答オープン読み上げ（音声ONの場合のみ）
        if (soundEnabled && ttsReady) {
          const handleReveal = async () => {
            const revealed = game.current?.revealed;
            const letters = revealed?.letters || [];
            const isPerfect = revealed?.isPerfect;
            const reading = revealed?.answerReading || null; // 読み仮名

            // 「回答オープン！」→ 効果音 → アニメーション開始 → 読み上げ
            await readAnswerReveal(letters, isPerfect, () => {
              // 効果音再生後にアニメーション開始
              setRevealAnimation(true);
              setResultModalContent({ isPerfect });
              setShowResultModal(true);
            }, reading);
          };
          handleReveal();
        } else {
          // 音声OFFの場合はすぐにアニメーション開始
          const revealed = game.current?.revealed;
          setRevealAnimation(true);
          setResultModalContent({ isPerfect: revealed?.isPerfect });
          setShowResultModal(true);
        }
        break;

      case FL_PHASE.SOLUTION:
        phaseHandledRef.current[phaseKey] = true;
        // 不正解の場合、正解を読み上げ（音声ONの場合のみ）
        if (soundEnabled && ttsReady) {
          const handleSolution = async () => {
            const revealed = game.current?.revealed;
            const isPerfect = revealed?.isPerfect;
            const answerWord = revealed?.answerWord || game.current?.answer || [];
            const reading = revealed?.answerReading || null; // 読み仮名

            if (!isPerfect) {
              await readCorrectAnswer(answerWord, reading);
            } else {
              // 正解の場合は「次の問題です」
              const maxQuestions = room.config?.maxQuestions || 10;
              if (questionIndex < maxQuestions - 1) {
                await announceNextQuestion();
              }
            }
          };
          handleSolution();
        }
        break;

      default:
        phaseHandledRef.current[phaseKey] = true;
        clearInterval(timerRef.current);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [room?.game?.phase, room?.game?.questionIndex, room?.playerOrder, room?.players, room?.config, soundEnabled, ttsReady]);

  // ゲーム終了時のTTS（音声ONの場合のみ）
  useEffect(() => {
    if (room?.status === FL_ROOM_STATUS.FINISHED && !phaseHandledRef.current['finished']) {
      phaseHandledRef.current['finished'] = true;
      if (soundEnabled && ttsReady) {
        const game = room.game;
        const correctQuestions = game?.correctQuestions || 0;
        const maxQuestions = room.config?.maxQuestions || 10;
        announceGameClear(correctQuestions, maxQuestions);
      }
    }
  }, [room?.status, room?.game?.correctQuestions, room?.config?.maxQuestions, soundEnabled, ttsReady]);

  if (loading) {
    return (
      <div className="tv-container">
        <div className="tv-loading">読み込み中...</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="tv-container">
        <div className="tv-error">
          <p>ルームが見つかりません</p>
          <p className="tv-error-hint">URLを確認してください</p>
        </div>
      </div>
    );
  }

  // 待機中
  if (room.status === FL_ROOM_STATUS.WAITING) {
    const players = room.players || [];
    return (
      <div className="tv-container" onClick={soundEnabled && !ttsReady ? initTTS : undefined}>
        {/* 音声ON/OFF選択プロンプト（初回のみ） */}
        {showSoundPrompt && (
          <div className="tv-sound-prompt-overlay">
            <div className="tv-sound-prompt">
              <h2>🎙️ 音声読み上げ</h2>
              <p>問題やアナウンスを音声で読み上げます</p>
              <p className="tv-sound-prompt-sub">テレビ番組のような体験ができます！</p>
              <div className="tv-sound-prompt-buttons">
                <button
                  className="tv-sound-btn on"
                  onClick={() => toggleSound(true)}
                >
                  🔊 ONにする
                </button>
                <button
                  className="tv-sound-btn off"
                  onClick={() => toggleSound(false)}
                >
                  🔇 OFFにする
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="tv-waiting">
          <div className="tv-waiting-header">
            <h1>QUIZ ONE WORD</h1>
            <div className="tv-room-code">
              <span className="tv-room-code-label">ルームコード</span>
              <span className="tv-room-code-value">{room.roomCode}</span>
            </div>
          </div>
          <div className="tv-waiting-players">
            <h2>参加者 ({players.length}/6)</h2>
            <div className="tv-player-list">
              {players.map((player, idx) => (
                <div
                  key={player.playerId}
                  className={`tv-player-card ${player.ready ? 'ready' : ''}`}
                >
                  <span className="tv-player-num">{idx + 1}</span>
                  <span className="tv-player-name">{player.nickname}</span>
                  <span className="tv-player-status">
                    {player.ready ? 'OK!' : '...'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 音声トグル（常に表示） */}
          <div className="tv-sound-toggle">
            <button
              className={`tv-sound-toggle-btn ${soundEnabled ? 'on' : 'off'}`}
              onClick={(e) => { e.stopPropagation(); toggleSound(!soundEnabled); }}
            >
              {soundEnabled ? '🔊 音声ON' : '🔇 音声OFF'}
            </button>
            {soundEnabled && !ttsReady && (
              <span className="tv-sound-hint">タップして有効化</span>
            )}
          </div>

          {/* ミラーリング誘導 */}
          <button
            className="tv-mirror-btn"
            onClick={(e) => { e.stopPropagation(); setShowMirrorGuide(!showMirrorGuide); }}
          >
            📺 TVに映す方法
          </button>
          {showMirrorGuide && (
            <div className="tv-mirror-guide" onClick={(e) => e.stopPropagation()}>
              <p><b>iPhone/iPad:</b> コントロールセンター → 画面ミラーリング</p>
              <p><b>Mac:</b> コントロールセンター → 画面ミラーリング</p>
              <p><b>Windows:</b> Win+K → ワイヤレスディスプレイ</p>
              <p><b>Chromecast:</b> キャストボタン → デバイス選択</p>
            </div>
          )}

          {soundEnabled && ttsReady && (
            <button
              className="tv-voice-test-btn"
              onClick={(e) => { e.stopPropagation(); setShowVoiceTest(!showVoiceTest); }}
            >
              🎤 声テスト
            </button>
          )}
          {showVoiceTest && (
            <div className="tv-voice-test" onClick={(e) => e.stopPropagation()}>
              {GOOGLE_VOICES.map((v) => (
                <button
                  key={v.id}
                  className="tv-voice-btn"
                  onClick={() => testVoice(v.id, 'こんにちは')}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
          <p className="tv-waiting-hint">全員が準備完了するとゲーム開始</p>
        </div>
      </div>
    );
  }

  // ゲーム終了
  if (room.status === FL_ROOM_STATUS.FINISHED) {
    const game = room.game;
    const correctQuestions = game?.correctQuestions || 0;
    const maxQuestions = room.config?.maxQuestions || 10;
    const isPerfect = correctQuestions === maxQuestions;

    return (
      <div className="tv-container">
        <div className={`tv-result ${isPerfect ? 'perfect' : ''}`}>
          <h1 className="tv-result-title">
            {isPerfect ? 'パーフェクトクリア！' : 'ゲーム終了'}
          </h1>
          <div className="tv-result-score">
            <span className="tv-score-num">{correctQuestions}</span>
            <span className="tv-score-sep">/</span>
            <span className="tv-score-total">{maxQuestions}</span>
          </div>
          <p className="tv-result-label">問正解</p>
        </div>
      </div>
    );
  }

  // ゲーム中
  const game = room.game;
  const phase = game?.phase;
  const current = game?.current;
  const playerOrder = room.playerOrder || [];
  const submissions = current?.submissions || [];
  const revealed = current?.revealed;
  const letters = revealed?.letters || [];
  const correctMask = revealed?.correctMask || [];
  const answerWord = revealed?.answerWord || current?.answer || [];

  return (
    <div className="tv-container" onClick={soundEnabled && !ttsReady ? initTTS : undefined}>
      <div className="tv-game">
        {/* 音声トグル（右上に常に表示） */}
        <button
          className={`tv-game-sound-toggle ${soundEnabled ? 'on' : 'off'}`}
          onClick={(e) => { e.stopPropagation(); toggleSound(!soundEnabled); }}
        >
          {soundEnabled ? '🔊' : '🔇'}
        </button>

        {/* TTS未初期化の警告（音声ONの場合のみ） */}
        {soundEnabled && !ttsReady && (
          <div className="tv-tts-warning">🔊 クリックで音声有効化</div>
        )}
        {/* ヘッダー */}
        <div className="tv-header">
          <div className="tv-question-badge">
            <span className="tv-q-num">第{(game?.questionIndex || 0) + 1}問</span>
            <span className="tv-q-diff">{FL_DIFFICULTY[(game?.questionIndex || 0) + 1]?.label}</span>
          </div>
          <div className="tv-category">
            <span>{current?.category}</span>
            <span className="tv-script">{FL_SCRIPT_LABELS[current?.script]}</span>
          </div>
          <div className="tv-score">
            正解: {game?.correctQuestions || 0}問
          </div>
        </div>

        {/* 問題文 */}
        <div className="tv-prompt">
          <p>{current?.prompt}</p>
        </div>

        {/* 読み上げ中 */}
        {phase === FL_PHASE.READING && (
          <div className="tv-reading">
            <span className="tv-reading-icon">🎙️</span>
            <span className="tv-reading-text">問題を読み上げ中...</span>
          </div>
        )}

        {/* タイマー */}
        {phase === FL_PHASE.ANSWERING && (
          <div className={`tv-timer ${timeLeft <= 10 ? 'danger' : ''}`}>
            {timeLeft}
          </div>
        )}

        {/* カウントダウン */}
        {phase === FL_PHASE.REVEAL_COUNTDOWN && (
          <div className="tv-countdown">
            <span>{countdownNum}</span>
          </div>
        )}

        {/* 文字スロット */}
        <div className="tv-slots">
          {playerOrder.map((pid, idx) => {
            const player = room.players?.find(p => p.playerId === pid);
            const submission = submissions.find(s => s.index === idx);
            const isConfirmed = submission?.confirmedAt != null;

            // 回答フェーズ: ✓ or ＿
            // REVEALEDフェーズ: アニメーション前は「？」、アニメーション後は回答
            // SOLUTIONフェーズ: 正解
            let displayChar = '＿';
            if (phase === FL_PHASE.ANSWERING) {
              displayChar = isConfirmed ? '✓' : '＿';
            } else if (phase === FL_PHASE.REVEAL_COUNTDOWN) {
              displayChar = isConfirmed ? '✓' : '＿';
            } else if (phase === FL_PHASE.REVEALED) {
              displayChar = revealAnimation ? (letters[idx] || '＿') : '？';
            } else if (phase === FL_PHASE.SOLUTION) {
              displayChar = answerWord[idx]?.trim() || letters[idx] || '＿';
            } else {
              displayChar = letters[idx] || '＿';
            }

            const isCorrect = correctMask[idx];
            let slotClass = 'tv-slot';
            if ((phase === FL_PHASE.REVEALED && revealAnimation) || phase === FL_PHASE.SOLUTION) {
              slotClass += isCorrect ? ' correct' : ' incorrect';
              if (phase === FL_PHASE.REVEALED && revealAnimation) {
                slotClass += ' reveal-anim';
              }
            }

            // 文字種バッジ（REVEALED/SOLUTIONフェーズで表示）
            const charTypeLabel = (phase === FL_PHASE.REVEALED && revealAnimation) || phase === FL_PHASE.SOLUTION
              ? getCharTypeLabel(displayChar)
              : null;

            return (
              <div key={pid} className={slotClass} style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="tv-slot-char">{displayChar}</div>
                {charTypeLabel && <div className="tv-slot-type">{charTypeLabel}</div>}
                <div className="tv-slot-name">{player?.nickname || `P${idx + 1}`}</div>
              </div>
            );
          })}
        </div>

        {/* 正解表示（下部） */}
        {phase === FL_PHASE.SOLUTION && (
          <div className="tv-solution">
            <span className="tv-solution-label">正解</span>
            <span className="tv-solution-answer">
              {answerWord.map(c => c?.trim() || '').join('')}
            </span>
          </div>
        )}

        {/* 結果モーダル（上部に表示、回答部分に被らない） */}
        {showResultModal && (phase === FL_PHASE.REVEALED || phase === FL_PHASE.SOLUTION) && (
          <div className={`tv-result-modal ${resultModalContent.isPerfect ? 'perfect' : 'failed'}`}>
            {resultModalContent.isPerfect ? '🎉 正解！' : '😢 残念...'}
          </div>
        )}
      </div>
    </div>
  );
}

export default TVDisplay;
